/*
 * `useRunStatus(runId)` — subscribe to a run's live status stream over WS.
 *
 * Opens a reconnecting socket to `GET /ws/runs/{run_id}`, authenticated on the
 * handshake via the bearer subprotocol (token from the auth store). Each server
 * frame is `{ run_id, stage, status }`; the hook surfaces the latest frame, a
 * short history (useful for U24's per-stage timeline), and the live connection
 * status. U24 consumes this to render the state-machine progress as it advances.
 *
 * The socket is torn down on unmount, on `runId` change, and when the token is
 * absent (signed out). Passing `enabled: false` (or a null runId) skips
 * connecting entirely.
 */

import { useEffect, useRef, useState } from "react";
import { runStatusWsUrl } from "../api/config";
import { getToken, subscribe } from "../auth/tokenStore";
import {
  ReconnectingSocket,
  type SocketStatus,
} from "./ReconnectingSocket";

/** The frame the server streams for each run-status update. */
export interface RunStatusFrame {
  run_id: string;
  stage: string;
  status: string;
}

export interface UseRunStatusResult {
  /** Most recent frame received, or null before the first message. */
  latest: RunStatusFrame | null;
  /** Frames in arrival order (bounded to the last `historyLimit`). */
  history: RunStatusFrame[];
  /** Live connection state of the underlying socket. */
  connection: SocketStatus;
}

export interface UseRunStatusOptions {
  enabled?: boolean;
  /** Max frames retained in `history`. Default 100. */
  historyLimit?: number;
}

function isRunStatusFrame(value: unknown): value is RunStatusFrame {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.run_id === "string" &&
    typeof v.stage === "string" &&
    typeof v.status === "string"
  );
}

export function useRunStatus(
  runId: string | null | undefined,
  options: UseRunStatusOptions = {},
): UseRunStatusResult {
  const { enabled = true, historyLimit = 100 } = options;

  const [latest, setLatest] = useState<RunStatusFrame | null>(null);
  const [history, setHistory] = useState<RunStatusFrame[]>([]);
  const [connection, setConnection] = useState<SocketStatus>("closed");

  // Track the live token so a re-auth/logout reconnects with the new value.
  const [token, setTokenState] = useState<string | null>(() => getToken());
  useEffect(() => subscribe(setTokenState), []);

  const socketRef = useRef<ReconnectingSocket | null>(null);

  useEffect(() => {
    if (!enabled || !runId || !token) {
      setConnection("closed");
      return;
    }

    // Reset stream state when (re)subscribing to a different run/token.
    setLatest(null);
    setHistory([]);

    const socket = new ReconnectingSocket({
      url: runStatusWsUrl(runId),
      token,
      onMessage: (data) => {
        if (!isRunStatusFrame(data)) return;
        setLatest(data);
        setHistory((prev) => {
          const next = [...prev, data];
          return next.length > historyLimit
            ? next.slice(next.length - historyLimit)
            : next;
        });
      },
      onStatusChange: setConnection,
    });
    socketRef.current = socket;

    return () => {
      socket.close();
      socketRef.current = null;
    };
  }, [enabled, runId, token, historyLimit]);

  return { latest, history, connection };
}
